import { ChangeDetectorRef, Directive, ElementRef, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";

import { CustomerSupportService } from "./customer-support.service";

/**
 * Directive that can be used to easily create anchor links
 * for the customer support page.
 *
 * It automatically sets the anchor element's `href` and `target`
 * attribute, so you don't have to handles those yourself.
 *
 * @example
 * ```html
 * <a obgCustomerSupportLink>Open customer support page</a>
 * ```
 */
@Directive({
	selector: "a[obgCustomerSupportLink]",
	standalone: true,
})
export class CustomerSupportLinkDirective implements OnInit, OnDestroy {
	private pageUrl$$: Subscription | undefined;
	private removeClickListener: () => void = () => {};

	constructor(
		private elementRef: ElementRef,
		private renderer: Renderer2,
		private customerSupportService: CustomerSupportService,
		private changeDetectorRef: ChangeDetectorRef
	) {}

	ngOnInit() {
		this.pageUrl$$ = this.customerSupportService.customerSupportUrl$
			.pipe(
				tap((customerSupportUrl) => {
					this.setAttribute("href", customerSupportUrl);
					this.setAttribute("target", "_blank");
				})
			)
			.subscribe(() => this.changeDetectorRef.markForCheck());

		// Workaround: Support link opens twice on windows.
		this.removeClickListener = this.renderer.listen(this.elementRef.nativeElement, "click", (event: MouseEvent) => {
			event.preventDefault();
		});
	}

	ngOnDestroy(): void {
		this.pageUrl$$?.unsubscribe();
		this.removeClickListener();
	}

	private setAttribute(name: string, value: string) {
		this.renderer.setAttribute(this.elementRef.nativeElement, name, value);
	}
}
